import type { CSSProperties, MouseEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { PublishErrorAction } from '@unipostudio/shared-types';
import { PlatformMark } from './PlatformMark';
import type { PlatformId } from './PlatformIcon';
import { PLATFORM_LIST } from './platform-list';
import { Icons } from '@/shared/constants/icons';
import { usePlatformStatuses } from '@/shared/hooks/usePlatforms';
import { failureActionHref, usePublishFailureAction } from '@/shared/hooks/usePublishFailureAction';
import { describePublishFailure, platformName, type FailedResultLike } from '@/shared/lib/post-result-error';

interface PublishFailureProps {
  postId: string;
  result: FailedResultLike;
  /**
   * The screen has its own Retry for the whole post (quick edit): a failure
   * whose action is Retry shows no second one.
   */
  retryElsewhere?: boolean;
  /**
   * False when another failed channel of the post may already have it
   * (postRetryIsSafe): Retry resends every failed channel, so this one offers
   * a draft copy to edit instead.
   */
  retrySafe?: boolean;
  className?: string;
  style?: CSSProperties;
}

const ACTION_ICON: Record<PublishErrorAction, (typeof Icons)[keyof typeof Icons]> = {
  reconnect: Icons.refresh,
  edit_post: Icons.edit,
  retry: Icons.refresh,
  replace_media: Icons.media,
  buy_x_credits: Icons.billing,
  wait: Icons.refresh,
  check_on_platform: Icons.externalLink,
  contact_support: Icons.help,
  choose_plan: Icons.billing,
};

const isPlatformId = (id: string): id is PlatformId => PLATFORM_LIST.some((p) => p.id === id);

/** Clicks inside a row that opens something else stay here. */
const stop = (e: MouseEvent): void => e.stopPropagation();

/**
 * One channel's failed publish, the same on every screen (Task 1.1): the
 * platform mark, a title in `--bad`, a one-line reason, one action, and the
 * channel's own message under "Details". A post that may already be live is
 * never offered Retry; its action is to look on the channel first.
 */
export function PublishFailure({ postId, result, retryElsewhere = false, retrySafe = true, className = '', style }: PublishFailureProps): ReactElement {
  const { t } = useTranslation();
  const { data: statuses } = usePlatformStatuses();
  const { run, busy } = usePublishFailureAction();
  const text = describePublishFailure(result, t);
  const retries = text.copy.action === 'retry' || text.copy.action === 'wait';
  const action: PublishErrorAction = retries && !retrySafe ? 'edit_post' : text.copy.action;
  const platformId = result.platform.toLowerCase();
  const username = statuses?.find((s) => s.platform.toUpperCase() === result.platform.toUpperCase())?.username ?? null;
  const target = { postId, platform: result.platform, username };
  const href = failureActionHref(action, target);
  const showAction = !(retryElsewhere && (action === 'retry' || action === 'wait'));
  const label = t(`publishError.action.${action}`, { platform: platformName(result.platform) });
  const Icon = ACTION_ICON[action];

  return (
    <div className={`flex items-start gap-2.5 ${className}`} style={style} data-testid="publish-failure" data-action={action}>
      {isPlatformId(platformId) && <PlatformMark platform={platformId} size={20} className="mt-0.5 shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="m-0 text-ui font-semibold text-[var(--bad)]">{text.title}</p>
        <p className="m-0 mt-0.5 text-ui text-[var(--ink-2)]">{text.reason}</p>
        {showAction && (
          <div className="mt-2">
            {href ? (
              <a href={href} target="_blank" rel="noopener noreferrer" onClick={stop} className="btn btn-secondary btn-sm max-sm:min-h-11 inline-flex">
                <Icon size={13} aria-hidden="true" />
                {label}
              </a>
            ) : (
              <button
                type="button"
                onClick={(e) => { stop(e); void run(action, target); }}
                disabled={busy}
                aria-busy={busy || undefined}
                className="btn btn-secondary btn-sm max-sm:min-h-11"
              >
                <Icon size={13} aria-hidden="true" />
                {busy ? t('attention.working') : label}
              </button>
            )}
          </div>
        )}
        {text.details && (
          <details className="mt-1.5 text-meta text-[var(--ink-3)]" onClick={stop}>
            <summary className="cursor-pointer select-none w-fit">{t('publishError.details')}</summary>
            <p className="m-0 mt-1 break-words" dir="auto">{text.details}</p>
          </details>
        )}
      </div>
    </div>
  );
}
